import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { diContainer } from 'src/inversify.config';
import PaymentMethodsRepository from 'src/features/paymentMethods/data/PaymentMethodsRepository';
import useAuthContext from 'src/context/AuthProvider/useAuthContext';
import { PaymentMethodSlug } from 'src/types/common';
import { useGrowthbookSubdomainFlags } from 'src/common/hooks/useGrowthbookSubdomainFlags';

const paymentMethodsRepository = diContainer.get<PaymentMethodsRepository>(
  PaymentMethodsRepository
);

export default function usePaymentMethods() {
  const { user, session, isB2b } = useAuthContext();

  const query = useQuery({
    queryKey: ['paymentMethods', session],
    queryFn: () =>
      session &&
      user &&
      paymentMethodsRepository.getPaymentMethods({
        userId: user.pk,
        accessToken: session?.accessToken,
      }),
    enabled: !!user && !!session,
    placeholderData: keepPreviousData,
  });

  const cardMethodEnabled = useGrowthbookSubdomainFlags(
    'ENABLE_B2B_CARD_PAYMENT',
    false
  );
  const speiMethodEnabled = useGrowthbookSubdomainFlags(
    'ENABLE_B2B_SPEI_PAYMENT',
    false
  );
  const creditMethodEnabled = useGrowthbookSubdomainFlags(
    'ENABLE_B2B_CREDIT_PAYMENT',
    false
  );
  const pariTransferEnabled = useGrowthbookSubdomainFlags(
    'ENABLE_CUSTOM_SPEI_PAYMENT',
    false
  );
  const mercadoPagoMethodEnabled = useGrowthbookSubdomainFlags(
    'ENABLE_MP_WALLET_PAYMENT',
    false
  );
  const clipMethodEnabled = useGrowthbookSubdomainFlags(
    'ENABLE_CLIP_PAYMENT',
    true
  );
  const openPayMethodEnabled = useGrowthbookSubdomainFlags(
    'ENABLE_OPENPAY_PAYMENT',
    false
  );

  const paymentMethodFlags = {
    [PaymentMethodSlug.Card]: cardMethodEnabled,
    [PaymentMethodSlug.PariCredit]: isB2b ? creditMethodEnabled : false,
    [PaymentMethodSlug.BankTransfer]: isB2b ? speiMethodEnabled : false,
    [PaymentMethodSlug.PariTransfer]: isB2b ? pariTransferEnabled : false,
    [PaymentMethodSlug.MercadoPago]: mercadoPagoMethodEnabled,
    [PaymentMethodSlug.Clip]: clipMethodEnabled,
    [PaymentMethodSlug.OpenPay]: openPayMethodEnabled,
  };

  const isSomePaymentMethodEnabled =
    Object.values(paymentMethodFlags).includes(true);

  const paymentMethods = query.data;

  const availablePaymentMethods = paymentMethods?.filter(
    (method) => paymentMethodFlags[method.slug_name] === true
  );

  return {
    paymentMethods: query.data,
    paymentMethodFlags,
    isSomePaymentMethodEnabled,
    availablePaymentMethods,
    ...query,
  };
}
